import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { SiloTypes } from './CreateOrEditSiloForm';
import { TFunction } from 'i18next';
import { Button, makeAddressString, sortByProperty } from '@web/utils/';
import binIcon from '@web/utils/src/assets/icons/functions/bin-icon.svg';
import feedIcon from '@web/utils/src/assets/icons/functions/feed-icon.svg';
import { StyledTable } from '@/commons/ui/theme/materialStyles';
import { Address, Silo, Sort } from '@/types';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';

type PSilo = Pick<
  Silo,
  'id' | 'name' | 'icon' | 'capacity' | 'freeCapacity' | 'createdAt'
> & {
  address?:
    | Pick<
        Address,
        | 'addressLine1'
        | 'addressLine2'
        | 'addressPostcode'
        | 'addressLocality'
        | 'addressDistrict'
      >
    | undefined
    | null;
};

interface SilosTableProps {
  silos: PSilo[];
  handleEdit: (silo) => void;
  handleDelete: (silo) => void;
}

type TableKey = keyof PSilo;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'id',
    label: t('farm.silos.id'),
    alignRight: false,
  },
  {
    id: 'name',
    label: t('farm.silos.name'),
    alignRight: false,
  },
  {
    id: 'capacity',
    label: t('farm.silos.capacity'),
    alignRight: false,
  },
];

const SilosTable: React.FC<SilosTableProps> = (props) => {
  const { t } = useTranslation();
  const { silos, handleEdit, handleDelete } = props;

  return (
    <div className="py-2-0">
      <StyledTable>
        <TableHead>
          <TableRow>
            {TABLE_HEAD(t).map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.alignRight ? 'right' : 'left'}
                style={{ backgroundColor: 'white' }}
              >
                <span className="text-body1 font-rg capitalize">
                  {headCell.label}
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortByProperty(silos, 'createdAt', Sort.Desc).map((silo) => (
            <TableRow
              hover
              key={silo.id}
              tabIndex={-1}
              role="checkbox"
              className="relative"
            >
              <TableCell align="left">
                <div className="flex gap-2-0 items-center">
                  <ReactSVG
                    src={silo.icon === SiloTypes.FLAT ? binIcon : feedIcon}
                    className="w-4-0 h-4-0"
                    beforeInjection={(svg) => {
                      svg.setAttribute('style', 'height: 3.2rem; width:auto');
                    }}
                  />
                  <span className="text-1-4 font-md">{silo.id}</span>
                </div>
              </TableCell>
              <TableCell align="left">
                <p className="text-1-4 font-md">{silo.name}</p>
                {silo.address && (
                  <p className="text-1-4 font-md text-grey">
                    {makeAddressString(silo.address)}
                  </p>
                )}
              </TableCell>
              <TableCell align="left">
                <div className="flex justify-between">
                  <span className="text-1-4 font-md">
                    {silo.freeCapacity} / {silo.capacity}
                  </span>

                  <div>
                    <Button
                      className="scale-75"
                      onClick={() => handleEdit(silo)}
                    >
                      {t('farm.silos.edit')}
                    </Button>

                    <Button
                      className="scale-75"
                      onClick={() => handleDelete(silo)}
                      variant="error"
                    >
                      {t('farm.silos.delete')}
                    </Button>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
      {silos.length === 0 && (
        <p className="text-body1 py-4-0 font-bd text-center">
          {t('farm.silos.silos_not_found')}
        </p>
      )}
    </div>
  );
};
export default SilosTable;
